import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UsersRound, UserPlus, Shield, Lock, Power, X, Eye, EyeOff, ChefHat, Utensils, Store, Bike, ShoppingBag } from 'lucide-react';
import { getBackendUrl } from './utils/api';

const KDS_SOURCE_OPTIONS = [
  { id: 'mesa', label: 'Mesas', icon: Store },
  { id: 'delivery', label: 'Delivery', icon: Bike },
  { id: 'retirada', label: 'Retirada', icon: ShoppingBag },
];

const API = getBackendUrl() + '/api';

const ROLE_META = {
  gestor:      { label: 'Gestor',     color: 'from-fuchsia-500 to-purple-600', badge: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40' },
  caixa:       { label: 'Caixa',      color: 'from-emerald-500 to-green-600',   badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  garcom:      { label: 'Garçom',     color: 'from-amber-500 to-orange-600',    badge: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  cozinha:     { label: 'Cozinha',    color: 'from-red-500 to-rose-600',        badge: 'bg-red-500/15 text-red-300 border-red-500/40' },
  entregador:  { label: 'Entregador', color: 'from-sky-500 to-blue-600',        badge: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return '—'; }
}

const TeamManagementContent = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState(['caixa', 'garcom']);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSectorModal, setShowSectorModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/team/users`);
      setUsers(res.data.users || []);
      setAvailableRoles(res.data.available_roles || ['caixa', 'garcom']);
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Erro ao carregar equipe');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggleActive = async (u) => {
    if (u.is_owner) {
      toast.error('Não é possível desativar o gestor proprietário');
      return;
    }
    try {
      if (u.active) {
        await axios.delete(`${API}/team/users/${u.id}`);
        toast.success(`${u.name} desativado(a)`);
      } else {
        await axios.put(`${API}/team/users/${u.id}`, { active: true });
        toast.success(`${u.name} reativado(a)`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  return (
    <div className="space-y-6" data-testid="team-management-content">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
            <UsersRound className="w-7 h-7 text-fuchsia-400" />
            Equipe
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Crie acessos para caixa, garçom e cozinha com permissões limitadas.
          </p>
        </div>
        <button
          data-testid="team-add-button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white text-sm font-medium shadow-lg shadow-purple-900/30 transition-all"
        >
          <UserPlus className="w-4 h-4" />
          Novo membro
        </button>
      </div>

      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Carregando…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-gray-400">
            Nenhum membro cadastrado ainda.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {users.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.gestor;
              return (
                <div
                  key={u.id}
                  data-testid={`team-row-${u.role}`}
                  className="flex flex-wrap items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${meta.color} flex items-center justify-center font-bold text-white shadow-md`}>
                    {(u.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-white">{u.name || u.email}</span>
                      {u.is_owner && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/40 uppercase tracking-wide">
                          Proprietário
                        </span>
                      )}
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${meta.badge} uppercase tracking-wide`}>
                        {meta.label}
                      </span>
                      {!u.active && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/40 uppercase">
                          Inativo
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{u.email}</p>
                  </div>
                  <div className="text-xs text-gray-500 hidden md:block min-w-[150px]">
                    Último login<br />
                    <span className="text-gray-300">{formatDate(u.last_login_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {!u.is_owner && (
                      <>
                        {u.role === 'cozinha' && (
                          <button
                            data-testid={`team-sector-${u.id}`}
                            onClick={() => { setEditingUser(u); setShowSectorModal(true); }}
                            title="Configurar setor da cozinha"
                            className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 transition-colors"
                          >
                            <Utensils className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={`team-reset-pwd-${u.id}`}
                          onClick={() => { setEditingUser(u); setShowPasswordModal(true); }}
                          title="Redefinir senha"
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-indigo-300 transition-colors"
                        >
                          <Lock className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`team-toggle-${u.id}`}
                          onClick={() => handleToggleActive(u)}
                          title={u.active ? 'Desativar' : 'Reativar'}
                          className={`p-2 rounded-lg transition-colors ${
                            u.active
                              ? 'bg-rose-500/10 hover:bg-rose-500/20 text-rose-300'
                              : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300'
                          }`}
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-xs text-gray-500 px-2">
        <Shield className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
        Caixa vê pedidos + caixa + estoque (somente leitura).
        Garçom vê pedidos + mesas + cardápio (somente leitura).
        <span className="text-red-300"> Cozinha vê apenas o KDS com os itens do setor dele.</span>
        {' '}Apenas o gestor configura IA, MP, WhatsApp e equipe.
      </div>

      {showCreateModal && (
        <CreateUserModal
          availableRoles={availableRoles}
          onClose={() => setShowCreateModal(false)}
          onCreated={async () => { setShowCreateModal(false); await load(); }}
        />
      )}

      {showSectorModal && editingUser && (
        <SectorConfigModal
          user={editingUser}
          onClose={() => { setShowSectorModal(false); setEditingUser(null); }}
          onDone={async () => { setShowSectorModal(false); setEditingUser(null); await load(); }}
        />
      )}

      {showPasswordModal && editingUser && (
        <ResetPasswordModal
          user={editingUser}
          onClose={() => { setShowPasswordModal(false); setEditingUser(null); }}
          onDone={() => { setShowPasswordModal(false); setEditingUser(null); }}
        />
      )}
    </div>
  );
};

// ------------------ MODAIS ------------------

const CreateUserModal = ({ availableRoles, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: availableRoles[0] || 'caixa', kds_categories: [], kds_products: [], kds_sources: [] });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error('Preencha nome, email válido e senha (mín. 6 caracteres)');
      return;
    }
    if (form.role === 'cozinha' && form.kds_categories.length === 0 && form.kds_products.length === 0) {
      toast.error('Selecione ao menos uma categoria ou produto para o setor da cozinha');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/team/users`, form);
      toast.success(`${form.name} criado(a) com sucesso!`);
      onCreated();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (Array.isArray(d) ? d.map(x => x.message || x.msg).join(' • ') : 'Erro ao criar usuário'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="team-create-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-fuchsia-400" /> Novo membro
        </h2>
        <p className="text-xs text-gray-400 mb-5">Crie um acesso com role limitada.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Nome completo</label>
            <input data-testid="team-create-name" type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="glass-input w-full" placeholder="Ex: Maria Silva" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Email</label>
            <input data-testid="team-create-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="glass-input w-full" placeholder="maria@loja.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Senha inicial (mín. 6 caracteres)</label>
            <div className="relative">
              <input data-testid="team-create-password" type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="glass-input w-full pr-10" placeholder="Senha do membro" />
              <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Compartilhe a senha com o membro. Você pode alterá-la a qualquer momento.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Função</label>
            <div className="grid grid-cols-2 gap-2">
              {availableRoles.map(role => {
                const meta = ROLE_META[role] || ROLE_META.gestor;
                return (
                  <label key={role} data-testid={`team-create-role-${role}`}
                    className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${form.role === role ? 'border-fuchsia-500 bg-fuchsia-500/10' : 'border-white/10 bg-black/20 hover:border-white/30'}`}>
                    <input type="radio" name="role" value={role} className="sr-only" checked={form.role === role} onChange={() => setForm({ ...form, role })} />
                    <div className="font-medium text-white text-sm">{meta.label}</div>
                    <div className="text-[11px] text-gray-400 leading-tight mt-0.5">
                      {role === 'caixa' && 'Pedidos, caixa, estoque (leitura)'}
                      {role === 'garcom' && 'Mesas, pedidos, cardápio (leitura)'}
                      {role === 'cozinha' && 'Tela KDS com os itens do setor'}
                      {role === 'entregador' && 'Pedidos atribuídos'}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {form.role === 'cozinha' && (
            <SectorSelector categories={form.kds_categories} products={form.kds_products}
              onChange={(kds_categories, kds_products) => setForm(f => ({ ...f, kds_categories, kds_products }))} />
          )}
          {form.role === 'cozinha' && (
            <SourceSelector sources={form.kds_sources} onChange={(kds_sources) => setForm(f => ({ ...f, kds_sources }))} />
          )}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 text-sm">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="team-create-submit" className="flex-1 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 text-white text-sm font-medium disabled:opacity-60">
              {saving ? 'Criando…' : 'Criar membro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ResetPasswordModal = ({ user, onClose, onDone }) => {
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error('Senha precisa ter pelo menos 6 caracteres'); return; }
    try {
      setSaving(true);
      await axios.put(`${API}/team/users/${user.id}/password`, { new_password: pwd });
      toast.success('Senha redefinida!');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao redefinir senha');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="team-reset-pwd-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><Lock className="w-5 h-5 text-indigo-400" /> Redefinir senha</h2>
        <p className="text-xs text-gray-400 mb-4">Definir nova senha para <span className="text-white font-medium">{user.name}</span> ({user.email}).</p>
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <input data-testid="team-reset-pwd-input" type={show ? 'text' : 'password'} value={pwd} onChange={e => setPwd(e.target.value)} className="glass-input w-full pr-10" placeholder="Nova senha (mín. 6 caracteres)" autoFocus />
            <button type="button" onClick={() => setShow(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 text-sm">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="team-reset-pwd-submit" className="flex-1 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 text-white text-sm font-medium disabled:opacity-60">
              {saving ? 'Salvando…' : 'Salvar nova senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ------------------ SETOR DA COZINHA ------------------

const SectorSelector = ({ categories, products, onChange }) => {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProducts, setShowProducts] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [c, p] = await Promise.all([axios.get(`${API}/categories`), axios.get(`${API}/products`)]);
        setCats(c.data || []);
        setProds(p.data || []);
      } catch (e) {
        toast.error('Erro ao carregar categorias/produtos');
      } finally { setLoading(false); }
    })();
  }, []);

  const toggleCat = (id) => {
    const next = categories.includes(id) ? categories.filter(x => x !== id) : [...categories, id];
    onChange(next, products);
  };
  const toggleProd = (id) => {
    const next = products.includes(id) ? products.filter(x => x !== id) : [...products, id];
    onChange(categories, next);
  };

  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/[0.04] p-4 space-y-3" data-testid="sector-selector">
      <div className="flex items-center gap-2 text-red-300 text-sm font-semibold"><ChefHat className="w-4 h-4" /> Setor deste chefe</div>
      <p className="text-[11px] text-gray-400 -mt-1">Escolha as <b>categorias</b> que este chefe prepara. Ele verá no KDS somente os itens dessas categorias.</p>
      {loading ? (
        <div className="text-xs text-gray-400 py-2">Carregando…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {cats.length === 0 && <span className="text-xs text-gray-500">Nenhuma categoria cadastrada.</span>}
            {cats.map(c => {
              const on = categories.includes(c.id);
              return (
                <button key={c.id} type="button" data-testid={`sector-cat-${c.id}`} onClick={() => toggleCat(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${on ? 'bg-red-500 text-white border-red-500' : 'bg-black/20 text-gray-300 border-white/10 hover:border-red-400/50'}`}>
                  {on ? '✓ ' : ''}{c.name}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={() => setShowProducts(s => !s)} className="text-[11px] text-red-300 hover:text-red-200 underline underline-offset-2">
            {showProducts ? 'Ocultar' : '+ Adicionar produtos avulsos (opcional)'}
          </button>
          {showProducts && (
            <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-black/20 p-2 space-y-1">
              {prods.length === 0 && <span className="text-xs text-gray-500">Nenhum produto cadastrado.</span>}
              {prods.map(p => {
                const on = products.includes(p.id);
                return (
                  <label key={p.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5 cursor-pointer">
                    <input type="checkbox" checked={on} onChange={() => toggleProd(p.id)} className="accent-red-500" />
                    <span className="text-xs text-gray-200">{p.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SourceSelector = ({ sources, onChange }) => {
  const toggle = (id) => {
    const next = (sources || []).includes(id) ? (sources || []).filter(x => x !== id) : [...(sources || []), id];
    onChange(next);
  };
  const isAll = !sources || sources.length === 0;
  return (
    <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.04] p-4 space-y-3" data-testid="source-selector">
      <div className="flex items-center gap-2 text-sky-300 text-sm font-semibold"><Store className="w-4 h-4" /> De onde vêm os pedidos deste chefe</div>
      <p className="text-[11px] text-gray-400 -mt-1">Selecione os ambientes que este chefe atende. <b>Nada selecionado = todos os ambientes.</b></p>
      <div className="flex flex-wrap gap-2">
        {KDS_SOURCE_OPTIONS.map(opt => {
          const on = (sources || []).includes(opt.id);
          const Icon = opt.icon;
          return (
            <button key={opt.id} type="button" data-testid={`source-${opt.id}`} onClick={() => toggle(opt.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${on ? 'bg-sky-500 text-white border-sky-500' : 'bg-black/20 text-gray-300 border-white/10 hover:border-sky-400/50'}`}>
              <Icon className="w-3.5 h-3.5" />
              {on ? '✓ ' : ''}{opt.label}
            </button>
          );
        })}
      </div>
      {isAll && (<div className="text-[11px] text-sky-300/80">Atualmente: <b>Todos os ambientes</b></div>)}
    </div>
  );
};

const SectorConfigModal = ({ user, onClose, onDone }) => {
  const [categories, setCategories] = useState(user.kds_categories || []);
  const [products, setProducts] = useState(user.kds_products || []);
  const [sources, setSources] = useState(user.kds_sources || []);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (categories.length === 0 && products.length === 0) { toast.error('Selecione ao menos uma categoria ou produto'); return; }
    try {
      setSaving(true);
      await axios.put(`${API}/team/users/${user.id}`, { kds_categories: categories, kds_products: products, kds_sources: sources });
      toast.success('Setor atualizado!');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar setor');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="team-sector-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><Utensils className="w-5 h-5 text-red-400" /> Setor da cozinha</h2>
        <p className="text-xs text-gray-400 mb-4">Defina o que <span className="text-white font-medium">{user.name}</span> prepara. No KDS aparecerão só esses itens.</p>
        <div className="space-y-3">
          <SectorSelector categories={categories} products={products} onChange={(c, p) => { setCategories(c); setProducts(p); }} />
          <SourceSelector sources={sources} onChange={setSources} />
        </div>
        <div className="flex gap-2 pt-4">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 text-sm">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving} data-testid="team-sector-submit" className="flex-1 py-2 rounded-lg bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-500 text-white text-sm font-medium disabled:opacity-60">
            {saving ? 'Salvando…' : 'Salvar setor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamManagementContent;
